/**
 * Contact -- Section formulaire de contact + liens de contact directs.
 *
 * Role : permettre au visiteur (recruteur, client) d'entrer en contact.
 *
 * Statut actuel : le formulaire fonctionne via `mailto:` (submit ouvre le
 * client mail natif). Pas d'appel API back. Cette approche est simple mais
 * limitee (pas de tracking, pas de captcha, depend du client mail installe).
 *
 * Migration prevue (Sprint 002 avec l'endpoint contact + Sprint 003 avec BO) :
 *  1. Sprint 002 : `POST /api/v1/contact` sauvegarde le message en DB.
 *  2. Sprint 003 : BO permet a l'owner de voir les messages recus.
 *  3. Optionnel : ajouter reCAPTCHA v3 pour eviter le spam.
 *  4. Optionnel : envoi email de notification a l'owner via SMTP/SendGrid.
 *
 * Layout : 2 colonnes (formulaire + liens directs : email, LinkedIn, GitHub).
 */

import React from 'react';

const Contact: React.FC = () => {
  return (
    <section id="contact" className="py-16 bg-base-200 min-h-screen flex items-center">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-extrabold text-center mb-12">
          Contact
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Informations de contact */}
          <div className="card bg-base-100 shadow-xl p-8">
            <h3 className="text-2xl font-bold mb-6">Informations</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <div>
                  <p className="font-semibold">Email</p>
                  <a href="mailto:contact@example.com" className="text-primary hover:underline">anthonny@rca.mg</a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <div>
                  <p className="font-semibold">Téléphone</p>
                  <a href="tel:+33123456789" className="text-primary hover:underline">+261 34 23 879 34</a>
                </div>
              </div>
              <div className="pt-6">
                <h4 className="font-semibold mb-4">Réseaux sociaux</h4>
                <div className="flex gap-4">
                  <a href="https://www.linkedin.com/in/anthonny-a-a3210192/" target="_blank" rel="noopener noreferrer" className="btn btn-circle btn-outline">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M4.98 3.5c0 1.381-1.11 2.5-2.48 2.5s-2.48-1.119-2.48-2.5c0-1.38 1.11-2.5 2.48-2.5s2.48 1.12 2.48 2.5zm.02 4.5h-5v16h5v-16zm7.982 0h-4.968v16h4.969v-8.399c0-4.67 6.029-5.052 6.029 0v8.399h4.988v-10.131c0-7.88-8.922-7.593-11.018-3.714v-2.155z"/>
                    </svg>
                  </a>
                  <a href="https://github.com" target="_blank" rel="noopener noreferrer" className="btn btn-circle btn-outline">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                    </svg>
                  </a>
                  <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="btn btn-circle btn-outline">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Formulaire de contact */}
          <div className="card bg-base-100 shadow-xl p-8">
            <h3 className="text-2xl font-bold mb-6">Envoyez-moi un message</h3>
            <form className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text">Votre nom</span>
                  </label>
                  <input 
                    type="text" 
                    placeholder="Votre Nom" 
                    className="input input-bordered w-full" 
                    required 
                  />
                </div>
                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text">Votre email</span>
                  </label>
                  <input 
                    type="email" 
                    placeholder="name@company.com" 
                    className="input input-bordered w-full" 
                    required 
                  />
                </div>
              </div>
              <div className="form-control w-full">
                <label className="label">
                  <span className="label-text">Sujet</span>
                </label>
                <input 
                  type="text" 
                  placeholder="Le sujet de votre message" 
                  className="input input-bordered w-full" 
                  required 
                />
              </div>
              <div className="form-control w-full">
                <label className="label">
                  <span className="label-text">Votre message</span>
                </label>
                <textarea 
                  className="textarea textarea-bordered h-32 w-full" 
                  placeholder="Votre message..." 
                  required
                ></textarea>
              </div>
              <div className="text-center">
                <button type="submit" className="btn btn-primary btn-wide">
                  Envoyer
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
